'use strict';

// Config
import config, { env } from 'config';

// Vue
import Vue from 'vue';

import { requestOrg, requestSearchOrg, requestServiceLinks } from 'api/org.js';

import DOMPurify from 'dompurify';

// 3rd party
import moment from 'moment';

// State
const state = {
  env,
  org: {},
  landingPopUp: true,
  selectLanguage: false,
  orgResults: [],
  page: {
    classes: '',
    name: '',
    params: {},
    meta: {},
    query: {},
    transitionDuration: null,
  },
  state: {
    init: false,
    loading: true,
    loaded: false,
  },
  viewport: {
    height: null,
    width: null,
  },
  touch: null,
  messagesOverride: null,
  orgLoading: 'initial',
};

// Getters
const getters = {
  org: state => state.org,
  orgResults: state => state.orgResults,
  orgLoading: state => state.orgLoading,
  appEnv: state => state.env,
  appState: state => state.state,
  appPage: state => state.page,
  viewportHeight: state => state.viewport.height,
  viewportWidth: state => state.viewport.width,
  viewportMobile: state => state.viewport.width < 768,
  touch: state => (state.touch === null ? null : !!state.touch),
  errorMessage: state => state.errorMessage,
  errorRedirectLink: state => state.errorRedirectLink || null,
  errorRedirectLabel: state => state.errorRedirectLabel || null,
  confirmMessage: state => state.confirmMessage || null,
  confirmDescription: state => state.confirmDescription || null,
  confirmButtonAction: state => state.confirmButtonAction || null,
  confirmButtonLink: state => state.confirmButtonLink || null,
  confirmButtonLabel: state => state.confirmButtonLabel || null,
  messagesOverride: state => state.messagesOverride, // TODO: Replace with messages
  serviceLinks: state => state.serviceLinks,
};

// Mutations
const mutations = {
  SET_PAGE(state, page) {
    state.page = page;
  },
  SET_STATE(state, key = '') {
    if (!key) return;

    Vue.set(state.state, key, true);
  },
  UNSET_STATE(state, key = '') {
    if (!key) return;

    Vue.set(state.state, key, false);
  },
  UPDATE_VIEWPORT(state, viewport) {
    state.viewport.height = viewport[0];
    state.viewport.width = viewport[1];
  },

  SET_TOUCH(state, touch) {
    state.touch = touch;
  },

  SET_ORG(state, payload) {
    Vue.set(state, 'org', payload);
  },

  SET_ORG_RESULTS(state, payload) {
    Vue.set(state, 'orgResults', payload);
  },

  SET_ORG_LOADING(state, payload) {
    Vue.set(state, 'orgLoading', payload);
  },

  SET_ERROR_MESSAGE(state, payload) {
    Vue.set(state, 'errorMessage', payload);
  },

  SET_ERROR_REDIRECT(state, payload) {
    Vue.set(state, 'errorRedirectLink', payload.link || null);
    Vue.set(state, 'errorRedirectLabel', payload.label || null);
  },

  SET_CONFIRM_MESSAGE(state, payload) {
    Vue.set(state, 'confirmMessage', payload);
  },

  SET_CONFIRM_DESCRIPTION(state, payload) {
    Vue.set(state, 'confirmDescription', payload);
  },

  SET_CONFIRM_BUTTON(state, payload) {
    Vue.set(state, 'confirmButtonAction', payload && payload.action || null);
    Vue.set(state, 'confirmButtonLink', payload && payload.link || null);
    Vue.set(state, 'confirmButtonLabel', payload && payload.label || null);
  },

  SET_MESSAGES(state, messages) {
    Vue.set(state, 'messagesOverride', messages);
  },

  SET_SERVICE_LINKS(state, payload) {
    Vue.set(state, 'serviceLinks', payload);
  },
  SET_SELECT_LANGUAGE(state, payload) {
    state.selectLanguage = payload;
  },
  SET_OFF_LOAD_POPUP(state) {
    state.landingPopUp = false;
  },
};

// Actions
const actions = {
  requestOrg({ commit , dispatch }) {
    return new Promise((resolve, reject) => {

      const path = window.location.pathname;
      const basefurl = config.frontendurl;
      const client = window.location.hostname.split('.').splice(0,1);
      const actualURL = window.location.href;

      const pageName = window.location.pathname.split('/').splice(-1)[0];

      if (path === '/' && window.location.hostname.match('mybeaconwellbeing.com')){
        const url = `https://${ basefurl }${ client}`;
        window.open(url ,'_self');
      }
      else if (window.location.hostname.match ('mybeaconwellbeing.com') ) {
        const queryparam = actualURL.split('?')[1];
        const url = `https://${ basefurl }${ client}${ path }?${queryparam}`;
        window.open(url ,'_self');
      }
      let clientId;
      const pathSegments = path.split('/').filter(segment => segment); // Filter out empty segments
      
      if (pathSegments.length > 0) {
        clientId = pathSegments[0];
        if (sessionStorage.getItem('clientId') !== clientId) {
          sessionStorage.setItem('clientId', clientId);
          window.location.pathname = window.location.pathname.toLowerCase();
          window.location.reload();
        }
      } else {
        clientId = window.location.host;
        sessionStorage.setItem('clientId', clientId);
        window.location.reload();
      }
      commit('SET_ORG_LOADING', 'loading');
      requestOrg(clientId)
        .then(response => {
          const data = response.data;
          const user = JSON.parse(localStorage.getItem('updateUser'));
          const org = data;
          if (!sessionStorage.getItem('fueledAuthTokenTimestamp')) {
            sessionStorage.setItem('fueledAuthTokenTimestamp', moment().format());
          }
          if (!sessionStorage.getItem('mdLiveAuthTokenTimestamp')) {
            sessionStorage.setItem('mdLiveAuthTokenTimestamp', moment().format());
          }

          if (org && org.dfd_client && pageName === 'log-in') { // Redirect to DFD if dfd_client is true
            const sanitizedDfdOrg = DOMPurify.sanitize(org.dfd_redirect_url, { ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.\\-]+(?:[^a-z+.\-:]|$))/i });
            window.location.href = `${sanitizedDfdOrg}/sign-in?cw=true`;
            return;
          }

          if (org.username === 'is_root_org') {
            localStorage.setItem('isRoot', true);
          } else {
            localStorage.removeItem('isRoot');
          }

          if (
            user &&
            user.organisation &&
            data.child_organisations.length > 0
          ) {
            const childOrg = data.child_organisations.filter(o => o.id === user.organisation)[0];

            org.number_of_sessions = childOrg.number_of_sessions;
            org.is_no_of_sessions_hidden = childOrg.is_no_of_sessions_hidden;
          }
          localStorage.removeItem('noOrg');
          dispatch('setOrg', org || {});
          resolve(response);
        })
        .catch(error => {
          localStorage.setItem('noOrg', true);
          console.error(error);
          reject(error);
        })
        .finally(() => {
          setTimeout(() => {
            commit('SET_ORG_LOADING', 'done');  
          }, 2000);          
        });
    });
  },

  requestSearchOrg({ dispatch }, id) {
    return new Promise((resolve, reject) => {
      requestSearchOrg(id)
        .then(response => {
          const results = response.data.results;
          dispatch('setOrgResults', results);
          resolve(response);
          return response;
        })
        .catch(error => {
          console.error(error);
          reject(error);
        });
    });
  },

  updatePage({ commit, rootState }) {
    const route = rootState.route;
    const classes = `page--${route.name}`;
    const duration = config.transitionDuration;

    commit('SET_PAGE', {
      name: route.name,
      fullPath: route.fullPath,
      params: route.params,
      meta: route.meta,
      query: route.query,
      classes,
      transitionDuration: duration,
    });

    commit('SET_MESSAGES', null);
  },

  openLoader({ commit }, payload = {}) {
    if (payload.messages) {
      commit('SET_MESSAGES', payload.messages);
    }

    commit('SET_STATE', 'loading');

    setTimeout(() => {
      commit('UNSET_STATE', 'loaded');
    }, state.page.transitionDuration);

  },

  closeLoader({ commit }) {
    commit('SET_STATE', 'init');
    commit('UNSET_STATE', 'loading');

    window.scrollTo(0, 0); // TODO: Fix on back button

    setTimeout(() => {
      commit('SET_STATE', 'loaded');
      commit('SET_MESSAGES', null);
    }, state.page.transitionDuration);
  },

  updateViewport({ commit }) {
    clearTimeout(window.updateViewport);

    // WHY: Debounces the update to only fire after all has been resized
    window.updateViewport = setTimeout(() => {
      commit('UPDATE_VIEWPORT', [window.innerHeight, window.innerWidth]);
    }, 100);
  },

  setTouch({ commit }) {
    const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints; // WHY: https://stackoverflow.com/a/4819886/2599650

    commit('SET_TOUCH', isTouch);
  },

  setOrg({ commit }, payload) {
    commit('SET_ORG', payload);
  },

  setOrgResults({ commit }, payload) {
    commit('SET_ORG_RESULTS', payload);
  },

  setErrorMessage({ commit }, payload) {
    commit('SET_ERROR_MESSAGE', payload);
  },

  setErrorRedirect({ commit }, payload) {
    commit('SET_ERROR_REDIRECT', payload);
  },

  setConfirmMessage({ commit }, payload) {
    commit('SET_CONFIRM_MESSAGE', payload);
  },

  setConfirmDescription({ commit }, payload) {
    commit('SET_CONFIRM_DESCRIPTION', payload);
  },

  setConfirmButton({ commit }, payload) {
    commit('SET_CONFIRM_BUTTON', payload);
  },

  setMessages({ commit }, payload) {
    commit('SET_MESSAGES', payload);
  },

  requestServiceLinks({ commit }, id) {
    const domain = window.location.pathname.split('/').splice(1,1);
    return new Promise((resolve, reject) => {
  
      requestServiceLinks(id, domain)
        .then(response => {
          commit('SET_SERVICE_LINKS', response.data);
          resolve(response);
        })
        .catch(error => {
          console.error(error);
          reject(error);
        });
    });
  },
  setselectLanguage({ commit }, payload) {
    commit('SET_SELECT_LANGUAGE', payload);
  },
  setOffLoadPopUp({ commit }) {
    commit('SET_OFF_LOAD_POPUP');
  },

};

// Export
export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};



// WEBPACK FOOTER //
// ./client/store/app.js